import { Theme, ThemeProvider, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { lightTheme } from "styles/theme";
import { ColorProp } from "styles/themes";

import UIcon from "lib/icons/untitled_ui/UIcon";

export type SpinnerSize = keyof Theme["size"];

type SpinnerProps = {
  size?: SpinnerSize;
  color?: ColorProp;
  label?: string;
  className?: string;
};

/**
 * The loading spinner primitive: Untitled UI loading-02 at a design-system size.
 *
 * Defaults to placeholder grey so a spinner reads as quiet "waiting" wherever it is dropped.
 * Pass `color="currentColor"` when it sits inline in text and should match it.
 *
 * @param size - Token from {@link Theme.size}
 * @param color - Colour from {@link Theme.colors}, or a raw CSS colour
 * @param label - Accessible name and screen-reader text; describe what is loading
 */
function Spinner(props: SpinnerProps) {
  const theme = useTheme();

  // Suspense fallbacks can render above the app's ThemeProvider, where the theme is empty and
  // reading a size or colour token would throw.
  if (!("size" in theme)) {
    return (
      <ThemeProvider theme={lightTheme}>
        <Spinner {...props} />
      </ThemeProvider>
    );
  }

  const { size = "size16", color = "sysTextPlaceholder", label = "Loading", className } = props;

  return (
    <Wrapper role="status" aria-label={label} className={className}>
      <UIcon icon="spinner" size={parseInt(theme.size[size], 10)} color={color} />
      <HiddenLabel>{label}</HiddenLabel>
    </Wrapper>
  );
}

export default Spinner;

// inline-flex so the wrapper hugs the glyph instead of picking up line-height whitespace.
const Wrapper = styled.span({
  display: "inline-flex",
  alignItems: "center",
});

// A role="status" region announces changes to its contents, so the label has to be real text
// inside it rather than only an aria-label. The sr-only clip pattern hides it visually.
const HiddenLabel = styled.span({
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
});
